'use client'

import { Input } from '@/components/ui/input'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { apiPatch, apiPost, useSources, useTenants } from './entriesApi'
import type { SourceItem } from './entryTypes'
import { trackEvent } from '@/lib/analytics'
import { SearchableMultiSelect } from '@shared/searchable-multi-select'
import { ManageEntityView, ManageEntityField, type ManageEntitySubmitResult } from './ManageEntityView'

type SourceForm = { nameCode: string; name: string; tenantIds: string[] }

const EMPTY_SOURCE_FORM: SourceForm = { nameCode: '', name: '', tenantIds: [] }

function formFromSource(s: SourceItem): SourceForm {
  return {
    nameCode: s.name_code,
    name: s.name,
    tenantIds: s.tenant_ids ?? [],
  }
}

export function ManageSourcesView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const { sources, loading: sourcesLoading, refetch: refetchSources } = useSources()
  const { tenants, loading: tenantsLoading } = useTenants()

  const onSubmit = async (form: SourceForm, mode: 'create' | 'edit', id: string | number | undefined): Promise<ManageEntitySubmitResult> => {
    const payload = {
      nameCode: form.nameCode,
      name: form.name,
      tenantIds: form.tenantIds,
    }

    const result = mode === 'create'
      ? await apiPost<{ id: string }>('/sources', payload)
      : await apiPatch('/sources', { ...payload, id })

    if (!result.success) {
      const errorMessage = typeof result.error === 'string' ? result.error : 'Failed to save'
      trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_source_submit' : 'update_source_submit', component: 'manage_source_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: errorMessage.slice(0, 200) })
      return { success: false, error: errorMessage }
    }

    trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_source_submit' : 'update_source_submit', component: 'manage_source_form', field_1_key: 'step', field_1_value: 'success' })
    return { success: true, id: mode === 'create' ? result.data?.id : id }
  }

  return (
    <ManageEntityView<SourceItem, SourceForm>
      theme={theme}
      title="Sources"
      createLabel="Create Source"
      items={sources}
      loading={sourcesLoading}
      getId={(s) => s.id}
      getName={(s) => s.name}
      getCode={(s) => s.name_code}
      getCreatedAt={(s) => s.created_at}
      getUpdatedAt={(s) => s.updated_at}
      emptyForm={EMPTY_SOURCE_FORM}
      formFromItem={formFromSource}
      isFormValid={(form) => !!form.nameCode && !!form.name}
      invalidMessage="Fill in Name Code and Name"
      onSubmit={onSubmit}
      onAfterSuccess={refetchSources}
      renderFields={(form, setForm) => (
        <>
          <ManageEntityField label="Name Code *" theme={theme}>
            <Input className={c.input} value={form.nameCode} onChange={(e) => setForm((p) => p && { ...p, nameCode: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Name *" theme={theme}>
            <Input className={c.input} value={form.name} onChange={(e) => setForm((p) => p && { ...p, name: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Tenants" theme={theme}>
            <SearchableMultiSelect
              theme={theme}
              loading={tenantsLoading}
              placeholder="Select tenants…"
              searchPlaceholder="Search by code or name…"
              options={tenants.map((t) => ({ id: t.id, code: t.name_code, label: t.name }))}
              selectedIds={form.tenantIds}
              onChange={(ids) => setForm((p) => p && { ...p, tenantIds: ids as string[] })}
            />
          </ManageEntityField>
        </>
      )}
    />
  )
}
